import { graphql } from "gatsby"
import {
  ContentfulRichTextGatsbyReference,
  RenderRichTextData,
} from "gatsby-source-contentful/rich-text"
import React from "react"
import Block from "../../atoms/layout-primitives/block"
import Grid from "../../atoms/layout-primitives/grid"
import { BackgroundColourContextProvider } from "../../util/background-colour-context"
import SideBySideMedia, { TSideBySideMediaComponent } from "./media"
import SideBySideText from "./text"

type TMediaAlignment = "Left" | "Right"

export const SIDE_BY_SIDE_CONTENT_TYPENAME = "ContentfulSideBySideContent"
export interface ISideBySideContent {
  __typename: typeof SIDE_BY_SIDE_CONTENT_TYPENAME
  text: RenderRichTextData<ContentfulRichTextGatsbyReference>
  content: TSideBySideMediaComponent
  contentPosition: TMediaAlignment
  backgroundColour?: {
    hex: string
  }
}

/** Renders rich text side by side with media content */
const SideBySideContent = ({
  text,
  backgroundColour,
  content,
  contentPosition,
}: Omit<ISideBySideContent, "__typename">) => {
  const textAlignment =
    contentPosition === "Left" // Text right
      ? {
          col: { sm: "1 / -1", md: "2 / -2", lg: "-7 / -1" },
        }
      : // Text left
        {
          col: { sm: "1 / -1", md: "2 / -2", lg: "1 / 7" },
        }

  const contentAlignment =
    contentPosition === "Left"
      ? {
          col: { sm: "1 / -1", md: "3 / -3", lg: "1 / 7" },
        }
      : // Media right
        {
          col: { sm: "1 / -1", md: "3 / -3", lg: "-7 / -1" },
        }

  return (
    <BackgroundColourContextProvider colour={backgroundColour?.hex}>
      <Grid>
        <Block
          row={{ sm: "2", lg: "1" }}
          align={{ lg: "center" }}
          {...textAlignment}
        >
          <SideBySideText text={text} />
        </Block>
        <Block
          row={{ sm: "1", lg: "1" }}
          align={{ lg: "center" }}
          {...contentAlignment}
        >
          <SideBySideMedia component={content} />
        </Block>
      </Grid>
    </BackgroundColourContextProvider>
  )
}

export const SideBySideContentFragment = graphql`
  fragment ContentfulSideBySideContent on ContentfulSideBySideContent {
    contentful_id
    text {
      raw
      references {
        __typename
        ... on ContentfulButton {
          ...ContentfulButton
        }
        ... on ContentfulTestimonial {
          ...ContentfulTestimonial
        }
        ... on ContentfulResponsiveImage {
          ...ContentfulResponsiveImage
        }
        ... on ContentfulContentBlockWithIcon {
          ...ContentfulContentBlockWithIcon
        }
      }
    }
    contentPosition
    content {
      __typename
      ... on ContentfulResponsiveImage {
        ...ContentfulResponsiveImage
      }
      ... on ContentfulCarousel {
        contentful_id
        images {
          ...ContentfulResponsiveImage
        }
      }
      ... on ContentfulVideo {
        ...ContentfulVideo
      }
    }
    backgroundColour {
      hex
    }
  }
`
export default SideBySideContent
